<template>
  <div class="artist">
    <div
      class="artist__background"
      :style="{
        backgroundImage: `url(${artist.header_image})`,
      }"
    ></div>

    <div class="artist__content">
      <div class="artist__details-box">
        <div class="artist__details-box_tags">
          <Tag class="tag--text tag--public" :value="artist.region" />
        </div>
        <h2 class="artist__details-box_title">{{ artist.name }}</h2>
        <p class="artist__details-box_sub-title">{{ artist.yearsLocation }}</p>
        <div
          class="artist__details-box_details collect-editor collect-editor--white"
          v-html="artist.biography"
        ></div>
      </div>

      <div v-if="hasArtworks" class="artist__title-box">
        <h2 class="artist__title-box_title">Art by {{ artist.name }}</h2>
        <div class="artist__title-box_right">
          <div class="artist__title-box_right-content">
            <PublicSelect
              :items="sortData"
              :selected="cardData.sort"
              @input="selectSort"
            ></PublicSelect>
            <FilterBlock
              style="margin-left: 15px"
              :items="filters"
              @open="openFilters = $event"
              @selectFilter="selectFilter"
              @apply="refreshSelectedFilters"
              @clear="refreshSelectedFilters(true)"
            ></FilterBlock>
          </div>
        </div>
      </div>

      <div v-if="hasArtworks && relatedArtworks.length" class="artist__artworks-box">
        <router-link
          v-for="(artwork, key) in relatedArtworks"
          :key="`${key}-artwork`"
          :to="{ path: `/artworks/${artwork.slug}` }"
          v-slot="{ navigate }"
          custom
        >
          <div class="artist__artworks-box_card" @click="navigate">
            <div class="image-box">
              <img class="image" :src="artwork.search_image" />
              <!-- <div v-if="artwork.sku" class="code">{{ artwork.sku }}</div> -->
              <div class="hover-container">Click to View</div>
            </div>
            <h3 class="title">{{ artwork.title }}</h3>
            <!-- <span class="price">{{ artwork.price }}</span> -->
            <span class="author">{{ artwork.artist }}</span>
          </div>
        </router-link>
      </div>
      <h6 v-if="hasArtworks && !relatedArtworks.length" class="artist__empty">No artworks</h6>

      <div v-if="hasArtworks && hasMoreArtworksPages" class="artist__button-box">
        <BaseButton
          class="button button--xs button--white"
          text="LOAD MORE ARTWORKS"
          @click.native="loadMoreArtworks"
        />
      </div>

      <div v-if="hasPublications" class="artist__publications-box">
        <h2 class="artist__publications-box_title">Publications featuring this artist</h2>
        <a
          v-for="(publication, key) in relatedPublications"
          :key="`${key}-publication`"
          class="artist__publications-box_item"
          :class="{ 'no-clickable': !publication.slug }"
          :href="publication.slug"
          target="_blank"
        >
          <span class="title">{{ publication.title }}</span>
          <span class="author">{{ publication.type }} by {{ publication.author }}</span>
        </a>
        <BaseButton
          v-if="hasMorePublicationsPages"
          class="button button--xs button--white"
          text="LOAD MORE PUBLICATIONS"
          @click.native="loadMorePublications"
        />
      </div>

      <div v-if="artist.past_shows_and_fair_booths" class="artist__cv-highlights">
        <h2 class="artist__cv-highlights_title">CV highlights</h2>
        <div
          class="artist__cv-highlights_text collect-editor collect-editor--white"
          v-html="artist.past_shows_and_fair_booths"
        ></div>
        <BaseButton
          v-if="artist.view_more_url"
          class="button button--xs button--white"
          text="VIEW MORE"
          @click="changeLocation(artist.view_more_url)"
        />
      </div>

      <!-- <div v-if="hasExhibition && relatedExhibitions.length" class="artist__title-box">
        <h2 class="artist__title-box_title">Appeared in browngrotta arts exhibition</h2>
      </div> -->

      <!-- <div v-if="hasExhibition && relatedExhibitions.length" class="artist__exhibitions-box">
        <router-link
          v-for="(exhibition, key) in relatedExhibitions"
          :key="`${key}-exhibition`"
          :to="{ path: `/exhibitions/${exhibition.slug}` }"
          v-slot="{ navigate }"
          custom
        >
          <div class="artist__exhibitions-box_card" @click="navigate">
            <div class="image-box">
              <img class="image" :src="exhibition.search_image" />
              <div class="hover-container">Click to View</div>
            </div>
            <div class="type">{{ exhibition.exhibition_type }}</div>
            <h3 class="title">{{ exhibition.title }}</h3>
            <span class="data">{{ exhibition.dates }}</span>
          </div>
        </router-link>
      </div>
      <div v-if="hasExhibition && hasMoreExhibitionsPages" class="artist__button-box">
        <BaseButton
          class="button button--xs button--white"
          text="LOAD MORE EXHIBITIONS"
          @click.native="loadMoreExhibitions"
        />
      </div> -->
    </div>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from "vuex";

import Tag from "@/components/Tag.vue";
import BaseButton from "@/components/buttons/BaseButton.vue";
import PublicSelect from "@/components/inputs/PublicSelect.vue";
import FilterBlock from "@/components/FilterPublic.vue";

export default {
  components: { Tag, BaseButton, PublicSelect, FilterBlock },

  data() {
    return {
      sortData: [
        { id: "availability_a_z", title: "Availability (A-Z)" }, // title_a_z
        { id: "availability_z_a", title: "Availability (Z-A)" }, // title_z_a
      ],
      cardData: {
        page: 1,
        sort: "availability_a_z", // title_a_z
        materials: "",
        price_start: "",
        price_end: "",
        colors: "",
      },
      filterBlock: {
        list: "materials,price,colors",
        type: "collection",
      },
      openFilters: false,
      publicationsPage: 1,
      exhibitionsPage: 1,
    };
  },

  head: {
    title: function () {
      return {
        inner: this.artist.name ?? "Artist browngrotta arts",
      };
    },

    meta: function () {
      return [
        {
          name: "description",
          itemprop: "description",
          content: `${this.artist.name ?? "Artist browngrotta arts"} - ${
            this.artist.description ?? "Description Artist browngrotta arts"
          }`,
          id: "meta:description",
        },
        {
          itemprop: "name",
          content: this.artist.name ?? "Artist browngrotta arts",
          id: "meta:name",
        },
        {
          itemprop: "image",
          content:
            this.artist.image ??
            `${process.env.VUE_APP_FRONTEND_URL}/img/icons/favicon-500x500.png`,
          id: "meta:image",
        },
        {
          property: "og:url",
          content: `${process.env.VUE_APP_FRONTEND_URL}${this.$route.path}`,
          id: "og:url",
        },
        {
          property: "og:type",
          content: "website",
          id: "og:type",
        },
        {
          property: "og:title",
          content: this.artist.name ?? "Artist browngrotta arts",
          id: "og:title",
        },
        {
          property: "og:image",
          content:
            this.artist.image ??
            `${process.env.VUE_APP_FRONTEND_URL}/img/icons/favicon-500x500.png`,
          id: "og:image",
        },
        {
          property: "og:image:alt",
          content: this.artist.name ?? "Artist browngrotta arts",
          id: "og:image:alt",
        },
        {
          property: "og:description",
          content: this.artist.description ?? "Description Artist browngrotta arts",
          id: "og:description",
        },
        {
          property: "og:site_name",
          content: process.env.VUE_APP_FRONTEND_URL,
          id: "og:site_name",
        },
        {
          property: "og:locale",
          content: "en_US",
          id: "og:locale",
        },
        {
          name: "twitter:card",
          content: "summary",
          id: "twitter:card",
        },
        {
          name: "twitter:site",
          content: "@BrowngrottaArts",
          id: "twitter:site",
        },
        {
          name: "twitter:creator",
          content: "@BrowngrottaArts",
          id: "twitter:creator",
        },
        {
          name: "twitter:url",
          content: `${process.env.VUE_APP_FRONTEND_URL}${this.$route.path}`,
          id: "twitter:url",
        },
        {
          name: "twitter:title",
          content: this.artist.name ?? "Artist browngrotta arts",
          id: "twitter:title",
        },
        {
          name: "twitter:description",
          content: this.artist.description ?? "Description Artist browngrotta arts",
          id: "twitter:description",
        },
        {
          name: "twitter:image",
          content:
            this.artist.image ??
            `${process.env.VUE_APP_FRONTEND_URL}/img/icons/favicon-500x500.png`,
          id: "twitter:image",
        },
        {
          name: "twitter:image:alt",
          content: this.artist.name ?? "Artist browngrotta arts",
          id: "twitter:image:alt",
        },
      ];
    },
  },

  computed: {
    ...mapGetters("publicapi/artists", {
      artist: "getArtist",
      hasArtworks: "getHasArtworks",
      hasPublications: "getHasPublications",
      relatedArtworks: "getRelatedArtworks",
      hasMoreArtworksPages: "getHasMoreArtworksPages",
      relatedPublications: "getRelatedPublications",
      hasMorePublicationsPages: "getHasMorePublicationsPages",
    }),
    ...mapGetters("publicapi/filters", {
      filters: "getFilters",
      selectedFilters: "getSelectedFilters",
    }),
  },

  async created() {
    await this.apiGetSpecificArtist({ slug: this.$route.params.slug });
    this.$emit("updateHead");
    this.apiGetFiltersBlock(this.filterBlock);
    this.apiGetRelatedArtworks({ ...this.cardData, slug: this.artist.slug });
    this.apiGetRelatedPublications({ slug: this.artist.slug, page: this.publicationsPage });

    const breadcrumbs = [
      {
        title: "Home",
        to: "/",
      },
      {
        title: "Artists",
        to: "/artists",
        clickable: true,
      },
      {
        title: `${this.artist.name}`,
        to: `/artists/${this.artist.slug}`,
        clickable: false,
      },
    ];
    this.setBreadcrumbs(breadcrumbs);

    this.setShareData({
      title: this.artist.name,
      description: this.artist.biography,
      image: this.artist.header_image,
    });
  },

  methods: {
    ...mapActions("publicapi/artists", [
      "apiGetSpecificArtist",
      "apiGetRelatedArtworks",
      "apiGetRelatedPublications",
      "apiGetRelatedExhibitions",
    ]),
    ...mapMutations("publicapi/share", ["setShareData"]),
    ...mapActions("publicapi/filters", ["apiGetFiltersBlock"]),
    ...mapMutations("publicapi/filters", ["updateFilters", "updateSelectedFilters", "clear"]),
    ...mapMutations(["setBreadcrumbs"]),

    async loadMoreArtworks() {
      if (this.hasMoreArtworksPages) {
        let data = { ...this.cardData, page: this.cardData.page + 1 };
        let success = await this.apiGetRelatedArtworks({ ...data, slug: this.artist.slug });
        if (success) {
          this.cardData = data;
        }
      }
    },
    async loadMorePublications() {
      if (this.hasMorePublicationsPages) {
        let success = await this.apiGetRelatedPublications({
          slug: this.artist.slug,
          page: this.publicationsPage++,
        });
        if (success) {
          this.publicationsPage++;
        }
      }
    },
    // async loadMoreExhibitions() {
    //   if (this.hasMoreExhibitionsPages) {
    //     let success = await this.apiGetRelatedExhibitions({
    //       slug: this.artist.slug,
    //       page: this.exhibitionsPage++,
    //     });
    //     if (success) {
    //       this.exhibitionsPage++;
    //     }
    //   }
    // },
    async selectSort({ id }) {
      let data = { ...this.cardData, page: 1, sort: id };
      let success = await this.apiGetRelatedArtworks({ ...data, slug: this.artist.slug });
      if (success) {
        this.cardData = { ...data };
      }
    },
    selectFilter(val) {
      this.updateFilters(val);
      if (val.apply) {
        this.refreshSelectedFilters();
      }
    },
    async refreshSelectedFilters(clear) {
      if (clear) {
        await this.clear();
      }
      await this.updateSelectedFilters();
      this.filterTable();
    },
    async filterTable() {
      let data = { ...this.cardData, page: 1 };
      this.selectedFilters.forEach(el => {
        if (el.key === "price") {
          data.price_start = "";
          data.price_end = "";
          el.items.forEach(i => {
            data[i.key] = i.val;
          });
        } else {
          let filters = el.items.map(i => {
            return i.id;
          });
          data[el.key] = filters.join(",");
        }
      });
      let success = await this.apiGetRelatedArtworks({ ...data, slug: this.artist.slug });
      if (success) {
        this.cardData = data;
      }
    },

    changeLocation(url) {
      if (url) window.open(url, "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  color: $white;
  font-size: 1.8rem;
  line-height: 2.4rem;
  @media screen and (max-width: $xs) {
    font-size: 1.6rem;
    line-height: 2.5rem;
  }
}

.artist {
  display: flex;

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;
    @media screen and (max-width: $xs) {
      height: 33.5rem;
    }
    &::before {
      content: "";
      position: absolute;
      width: 100%;
      height: 100%;
      background: rgba($color: $lightBlack, $alpha: 0.45);
      z-index: 1;
    }
    &::after {
      content: "";
      bottom: -1px;
      position: absolute;
      width: 100%;
      height: 50%;
      background: linear-gradient(
        0deg,
        rgba($color: $lightBlack, $alpha: 1) 0%,
        rgba($color: $lightBlack, $alpha: 0) 100%
      );
      z-index: 1;
    }
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: calc(100vh - 20.8rem) 10.5rem 24rem;
    z-index: 2;
    @media screen and (max-width: $xs) {
      padding: 24.5rem 2rem 24rem;
    }
  }

  &__details-box {
    display: flex;
    flex-direction: column;
    width: 60%;
    @media screen and (max-width: $md) {
      width: 100%;
    }

    &_tags {
      display: flex;
      margin: 0 0 10px;
      & > :first-child {
        margin-left: 0;
      }
    }

    &_title {
      font-weight: 500;
      font-size: 4rem;
      line-height: 5rem;
      letter-spacing: -1.33333px;
      margin-bottom: 10px;
      @media screen and (max-width: $xs) {
        font-size: 3.5rem;
        line-height: 4.3rem;
      }
    }
    &_sub-title {
      font-weight: 500;
      font-size: 2.4rem;
      line-height: 2.9rem;
      letter-spacing: -0.8px;
      margin-bottom: 15px;
      @media screen and (max-width: $xs) {
      }
    }

    .button {
      margin-top: 2.2rem;
      @media screen and (max-width: $xs) {
        margin-top: 3.5rem;
      }
    }
  }

  &__title-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin: 9rem 0rem 6rem;
    @media screen and (max-width: $lg) {
      flex-direction: column;
      align-items: flex-start;
    }
    @media screen and (max-width: $xs) {
      margin: 8.5rem 0rem 2rem;
    }
    &_right {
      //width: 100%;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      @media screen and (max-width: $md) {
        width: 100%;
        flex-direction: column;
        .divider {
          display: none;
        }
        &-content {
          &:last-of-type {
            margin-left: 10px;
          }
        }
      }
      &-content {
        width: 100%;
        display: flex;
        justify-content: flex-end;
        align-items: center;

        & > * {
          margin-bottom: 10px;
        }
      }
      @media screen and (max-width: $lg) {
        margin-top: 30px;
        margin-left: -10px;
      }
      @media screen and (max-width: $xs) {
        &-content {
          flex-wrap: wrap;
        }
      }
    }
    &_title {
      font-size: 4rem;
      line-height: 5rem;
      @media screen and (max-width: $xs) {
        font-size: 3.5rem;
        line-height: 4.3rem;
      }
    }
  }

  &__empty {
    font-size: 20px;
    font-weight: 600;
    padding: 50px 10px;
    text-align: center;
    text-transform: uppercase;
  }

  &__artworks-box {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(30%, 1fr));
    grid-column-gap: 3rem;
    grid-row-gap: 7rem;
    @media screen and (max-width: $md) {
      grid-template-columns: repeat(2, minmax(30%, 1fr));
    }
    @media screen and (max-width: $xs) {
      grid-template-columns: minmax(30%, 1fr);
      grid-row-gap: 7rem;
    }

    &_card {
      display: flex;
      flex-direction: column;
      cursor: pointer;

      &:hover {
        .image-box {
          .hover-container {
            opacity: 1;
          }
        }
      }

      .image-box {
        position: relative;

        .image {
          height: 27vw;
          width: 100%;
          object-fit: cover;
          background-size: cover;
          background-position: center;
          border-radius: 4px;
          @media screen and (max-width: $xs) {
            height: 90vw;
          }
        }

        // .code {
        //   position: absolute;
        //   right: 9px;
        //   bottom: 9px;
        //   text-transform: uppercase;
        //   border: 1px solid $white;
        //   border-radius: 5px;
        //   background-color: rgba(0, 0, 0, 0.45);
        //   min-width: 7.2rem;
        //   font-size: 1.4rem;
        //   line-height: 2.5rem;
        //   padding: 0.1rem 1rem;
        //   z-index: 2;
        //   max-width: 30%;
        //   display: block;
        //   white-space: nowrap;
        //   overflow: hidden;
        //   text-overflow: ellipsis;
        // }

        .hover-container {
          position: absolute;
          width: 100%;
          height: 100%;
          font-size: 3rem;
          line-height: 5rem;
          border-radius: 4px;
          background-color: rgba($color: $blue, $alpha: 0.83);
          display: flex;
          justify-content: center;
          align-items: center;
          transition: all 0.3s ease;
          opacity: 0;
          top: 0;
        }
      }

      .title {
        font-size: 2.4rem;
        line-height: 3.1rem;
        margin-top: 1rem;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        line-clamp: 2;
        text-overflow: ellipsis;
        overflow: hidden;

        // &:hover {
        //   text-decoration: underline;
        // }
      }

      // .price {
      //   font-size: 1.8rem;
      //   line-height: 2.8rem;
      // }

      .author {
        margin-top: 0.5rem;
        font-size: 1.4rem;
        line-height: 2.5rem;
        text-transform: uppercase;
        word-spacing: 3px;
        @media screen and (max-width: $xs) {
          margin-top: 1rem;
          font-size: 1.6rem;
          line-height: 2.8rem;
        }
      }
    }
  }

  &__button-box {
    display: flex;
    justify-content: center;
    align-content: center;
    width: 100%;
    margin-top: 7rem;

    .button {
      min-width: 180px;
    }
  }

  &__publications-box {
    display: flex;
    flex-direction: column;
    margin-top: 15rem;
    @media screen and (max-width: $xs) {
      margin-top: 9.5rem;
    }
    @media screen and (max-width: $xs) {
      align-items: center;
    }

    &_title {
      font-size: 4rem;
      line-height: 5rem;
      margin-bottom: 5rem;
      @media screen and (max-width: $xs) {
        font-size: 3.5rem;
        line-height: 4.3rem;
        margin-bottom: 5.4rem;
      }
    }

    &_item {
      display: flex;
      flex-direction: column;
      margin-bottom: 5rem;
      text-decoration: none;
      &.clickable {
        cursor: default;
      }

      .title {
        font-size: 2.4rem;
        line-height: 3.1rem;
      }
    }

    .button {
      margin-top: 1.3rem;
    }
  }

  &__cv-highlights {
    display: flex;
    flex-direction: column;
    //margin: 15rem 0rem 5rem;
    margin: 15rem 0rem 0rem;
    width: 70%;
    @media screen and (max-width: $md) {
      width: 100%;
    }
    @media screen and (max-width: $xs) {
      //margin: 10rem 0rem 2.5rem;
      margin: 10rem 0rem 0rem;
    }

    &_title {
      font-size: 4rem;
      line-height: 5rem;
      margin-bottom: 5rem;
      @media screen and (max-width: $xs) {
        font-size: 3.5rem;
        line-height: 4.3rem;
      }
    }

    &_text {
      margin-bottom: 3rem;
    }

    .button {
      min-width: 180px;
      @media screen and (max-width: $xs) {
        display: none;
      }
    }
  }

  // &__exhibitions-box {
  //   width: 100%;
  //   display: grid;
  //   grid-template-columns: 1fr 1fr 1fr;
  //   grid-column-gap: 3.9rem;
  //   grid-row-gap: 3.9rem;
  //   @media screen and (max-width: $md) {
  //     grid-template-columns: 1fr 1fr;
  //   }
  //   @media screen and (max-width: $xs) {
  //     grid-template-columns: 1fr;
  //     grid-column-gap: 25px;
  //     grid-row-gap: 25px;
  //   }

  //   &_card {
  //     display: flex;
  //     flex-direction: column;
  //     cursor: pointer;

  //     &:hover {
  //       .image-box {
  //         .hover-container {
  //           opacity: 1;
  //         }
  //       }
  //     }

  //     .image-box {
  //       position: relative;

  //       .image {
  //         height: 27vw;
  //         width: 100%;
  //         object-fit: cover;
  //         background-size: cover;
  //         background-position: center;
  //         border-radius: 4px;
  //         @media screen and (max-width: $md) {
  //           height: 40vw;
  //         }
  //         @media screen and (max-width: $xs) {
  //           height: 90vw;
  //         }
  //       }

  //       .hover-container {
  //         position: absolute;
  //         width: 100%;
  //         height: 100%;
  //         font-size: 3rem;
  //         line-height: 5rem;
  //         background-color: rgba($color: $blue, $alpha: 0.83);
  //         display: flex;
  //         justify-content: center;
  //         align-items: center;
  //         transition: all 0.3s ease;
  //         opacity: 0;
  //         top: 0;
  //         border-radius: 4px;
  //       }
  //     }

  //     .type {
  //       display: flex;
  //       justify-content: center;
  //       align-content: center;
  //       text-transform: uppercase;
  //       border: 1px solid $white;
  //       border-radius: 4px;
  //       width: max-content;
  //       min-width: 9.7rem;
  //       margin-top: 2.6rem;
  //       font-size: 1.4rem;
  //       line-height: 2.5rem;
  //       padding: 0.1rem 1rem;
  //     }

  //     .title {
  //       font-size: 2.4rem;
  //       line-height: 3.1rem;
  //       margin-top: 1rem;
  //       //cursor: pointer;
  //     }

  //     .data {
  //       margin-top: 1.2rem;
  //       font-size: 1.4rem;
  //       line-height: 2.5rem;
  //       text-transform: uppercase;
  //       @media screen and (max-width: $xs) {
  //         margin-top: 1rem;
  //         // font-size: 1.4rem;
  //         // line-height: 2.5rem;
  //       }
  //     }
  //   }
  // }
}
</style>
